import React, { useEffect, useState } from 'react';
import styled, { css } from 'styled-components';
import { DEVICE_SIZE, mq } from '../../../utils/mq';
import { useTranslation } from '../../../utils/translation';
import { useImageComponent, useLinkComponent } from '../../../utils/component';
import { Cta } from '../../cta';
import type { LinkProps } from '../../../atoms/types';
import Con from '../../../icons/con';
import Pro from '../../../icons/pro';
import ArrowPlainDown from '../../../icons/arrowPlainDown';
import { StarsMark } from '../../stars-with-mark';
import { HeadingTwo } from '../../../atoms/h2';
import { DetailedStickerType, PriceData } from '../../types';
import DetailedSticker from '../../../icons/sticker/detailedSticker';

export type HeroCardProps = {
  id?: number;
  size?: number;
  title?: string;
  rating?: number;
  pros?: any;
  cons?: any;
  image: { imageId: string; alt: string; extension?: string; title?: string };
  ctaLink?: LinkProps;
  urlCtaPrice?: string;
  ctaProps?: any;
  testLink?: LinkProps;
  prices?: any;
  productLink?: LinkProps;
  eventCta?: (shopName?: string, price?: string, position?: string, itemId?: number) => void;
  detailedSticker?: DetailedStickerType;
};

type CtaHeroCardProps = {
  price: PriceData;
  title: string;
  eventCta?: (shopName?: string, price?: string, position?: string) => void;
};

const Hero = styled.div`
  display: flex;
  flex-direction: column;
  margin: var(--spacer) 0;
`;

const HeroBox = styled.div`
  border-radius: 10px;
  box-shadow: 0px 0px 30px 0px var(--color-box-shadow);
  display: flex;
  position: relative;
  flex-direction: column;
  gap: var(--spacer-m-fluid);
  margin: 0 0 var(--spacer);
  padding: var(--spacer-s);

  ${mq.gte(
    DEVICE_SIZE.LARGE,
    css`
      padding: var(--spacer-m) var(--spacer-l);
    `,
  )}
`;

const Title = styled.div`
  font-size: 16px;
  font-family: var(--theme-typography-primary-font);
  font-weight: 700;
  line-height: 1.38;

  ${mq.gte(
    DEVICE_SIZE.LARGE,
    css`
      font-size: 22px;
    `,
  )}
`;

const HeroPriceTitle = styled(HeadingTwo)`
  font-size: 18px;
  font-family: var(--theme-typography-primary-font);
  font-weight: 700;
  line-height: 1.38;
  margin: 15px 0 0;

  ${mq.gte(
    DEVICE_SIZE.LARGE,
    css`
      font-size: 22px;
      margin: 0;
    `,
  )}
`;

const HeroTop = styled.div`
  display: flex;
  gap: var(--spacer-fluid);
  flex-flow: wrap;

  ${mq.gte(
    DEVICE_SIZE.LARGE,
    css`
      flex-direction: row;
      flex-flow: nowrap;
    `,
  )}
`;

const HeroImage = styled.div`
  min-width: 300px;

  ${mq.lte(
    768,
    css`
      min-width: 100%;

      picture {
        width: 100%;
      }

      img {
        object-fit: cover;
      }
    `,
  )}
`;

const HeroTitle = styled.div`
  font-family: var(--theme-typography-primary-font);
  font-size: 26px;
  font-weight: 700;
  line-height: 1.38;
  margin: 0 0 -15px;
  text-decoration: underline;
  transition: color 0.3s;

  &:hover {
    color: var(--theme-color-primary);
  }

  &.mod-no-link {
    text-decoration: initial;

    &:hover {
      color: initial;
    }
  }

  ${mq.gte(
    DEVICE_SIZE.LARGE,
    css`
      margin: 0 0 -30px;
    `,
  )}
`;

const HeroPrice = styled.div`
  position: relative;
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 7px;
`;

const HeroPriceTable = styled.div`
  display: flex;
  flex-direction: column;
`;

const HeroPriceLine = styled.div`
  align-items: center;
  border-bottom: solid 1px var(--color-alt-3);
  display: flex;
  gap: 15px;
  padding: 8px 0;

  &:first-of-type {
    font-weight: 700;
  }
`;

const HeroPriceLineLabel = styled.span`
  font-size: 16px;
  transition: color 0.3s;
  white-space: nowrap;

  &:hover {
    color: var(--theme-color-primary);
  }
`;

const HeroPriceLineCta = styled.div`
  margin-left: auto;
  min-width: 92px;

  a {
    white-space: nowrap;
  }
`;

const HeroPriceMore = styled.div`
  color: var(--text-color-alt);
  cursor: pointer;
  display: flex;
  font-family: var(--theme-typography-primary-font);
  font-size: 16px;
  font-weight: 700;
  gap: 10px;
  padding: 12px 0 0;

  ${mq.gte(
    DEVICE_SIZE.LARGE,
    css`
      font-size: 18px;
      padding: 12px 0;
    `,
  )}
`;

const HeroPriceMoreLabel = styled.span`
  text-decoration: underline;
  text-underline-offset: 2px;
`;

const LeftSide = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 15px;
`;

const RightSide = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 15px;
`;

const List = styled.ul`
  display: flex;
  flex-direction: column;
  font-size: 16px;
  gap: 12px;
  list-style: none;

  svg {
    min-width: 22px;
  }
`;

const Rotator = styled.div`
  tranform: initial;
  transition: all 0.3s;

  &.mod-rotated {
    transform: rotate(180deg);

    svg {
      position: relative;
      top: 6px;
    }
  }
`;

const HeroProsAndCons = styled.div`
  display: flex;
  flex-direction: column;
  margin: 0;
  gap: var(--spacer);

  ${mq.gte(
    DEVICE_SIZE.LARGE,
    css`
      flex-direction: row;
      margin: 0 var(--spacer-l);
    `,
  )}
`;

const ProItem = styled.li`
  align-items: start;
  display: flex;
  gap: var(--spacer-xs);
  line-height: 22px;
`;

const ConItem = styled.li`
  align-items: start;
  display: flex;
  gap: var(--spacer-xs);
  line-height: 22px;
`;

const RefurbishedCtaContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  gap: 12px;
`;

const RefurbishedLabelContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  line-height: 10px;
  margin-top: 4px;
  gap: 5px;
`;

const RefurbishedLabel = styled.div`
  font-size: 10px;
  line-height: 10px;
`;

const ShopLogo = styled.img`
  height: 24px;
  width: 24px;
`;

const WaitingShopLogo = styled.div`
  height: 24px;
  width: 24px;
`;

const ShopName = styled.div`
  display: flex;
  flex-direction: row;
  gap: 10px;
`;

const HeroReadConclusion = styled.div`
  display: flex;
  margin: var(--spacer) 0 0;

  ${mq.gte(
    DEVICE_SIZE.LARGE,
    css`
      margin: var(--spacer) var(--spacer-l) 0;
    `,
  )}
`;

const MobileAwardContainer = styled.div`
  position: absolute;
  z-index: 9;
  padding: initial;
  margin: initial;
  right: var(--spacer-s);
  top: calc(-1.7 * var(--spacer-s));

  ${mq.gte(
    DEVICE_SIZE.LARGE,
    css`
      display: none;
    `,
  )}
`;

const AwardContainer = styled.div`
  position: absolute;
  top: calc(-2 * var(--spacer-l));
  // right: calc(1.2 * var(--spacer-xs));
  right: 0;
  z-index: 9;
  padding: initial;
  margin: initial;

  ${mq.lte(
    DEVICE_SIZE.LARGE,
    css`
      display: none;
    `,
  )}
`;

const CtaHeroCard = ({ price, eventCta }: CtaHeroCardProps) => {
  const t = useTranslation();

  const ctaProps = {
    href: price.link,
    openInNewWindow: true,
    onClick: eventCta,
  };

  if (price.condition && price.condition !== 'NEW') {
    return (
      <Cta className="mod-sm" rel={['nofollow']} {...ctaProps}>
        <RefurbishedCtaContainer>
          <RefurbishedLabelContainer>
            {price.price || t('design.heroCard.cta')}
            <RefurbishedLabel>{t('design.refurbished')}</RefurbishedLabel>
          </RefurbishedLabelContainer>
        </RefurbishedCtaContainer>
      </Cta>
    );
  }

  return (
    <Cta className="mod-sm" rel={['nofollow']} {...ctaProps}>
      {price.price || t('design.heroCard.cta')}
    </Cta>
  );
};

const HeroCard = ({
  id,
  title,
  rating,
  pros,
  cons,
  image,
  testLink,
  prices,
  productLink,
  eventCta,
  detailedSticker,
}: HeroCardProps) => {
  const [seeMorePrice, setSeeMorePrice] = useState(false);
  const [lazyShopLogo, setLazyShopLogo] = useState(false);

  const t = useTranslation();
  const ImageComponent = useImageComponent();
  const LinkComponent = useLinkComponent();

  if (testLink.href && id) {
    if (!testLink.href.match(/#conclusion-product-[\d]+/)) {
      testLink.href += `#conclusion-product-${id}`;
    }
  }

  useEffect(() => {
    if (!lazyShopLogo) {
      setLazyShopLogo(true);
    }
  }, [lazyShopLogo, setLazyShopLogo]);

  const imageProps = {
    ...image,
    mobileWidth: 214,
    mobileHeight: 214,
    desktopWidth: 214,
    desktopHeight: 214,
  };

  const getMorePrice = () => {
    setSeeMorePrice(!seeMorePrice);
  };

  return (
    <Hero>
      <HeroBox>
        {detailedSticker && (
          <MobileAwardContainer>
            <DetailedSticker detailedSticker={detailedSticker} scale={0.17} />
          </MobileAwardContainer>
        )}
        {productLink.href ? (
          <HeroTitle>
            <LinkComponent {...productLink}>{`${title}`}</LinkComponent>
          </HeroTitle>
        ) : (
          <HeroTitle className="mod-no-link">{`${title}`}</HeroTitle>
        )}
        {rating && rating > 0 ? <StarsMark rating={rating / 2} max={5} ratingLabel={rating} maxLabel={10} /> : null}
        <HeroTop>
          <HeroImage>
            <ImageComponent {...imageProps} />
          </HeroImage>
          {prices && prices.length > 0 ? (
            <HeroPrice>
              {detailedSticker && (
                <AwardContainer>
                  <DetailedSticker detailedSticker={detailedSticker} scale={0.17} />
                </AwardContainer>
              )}
              <HeroPriceTitle>{t('design.reviewCard.bestPrice')}</HeroPriceTitle>
              <HeroPriceTable>
                {prices.map((price: PriceData, index: number) =>
                  index < 4 || seeMorePrice === true ? (
                    <HeroPriceLine key={price.link}>
                      <HeroPriceLineLabel>
                        <LinkComponent
                          href={price.link}
                          onClick={() => eventCta(price.price, price.shop.name, 'top-in-content', id)}
                          rel={['nofollow']}
                          openInNewWindow
                        >
                          <ShopName>
                            {price?.shop?.icon && lazyShopLogo && (
                              <ShopLogo src={price.shop.icon} alt={price?.shop?.name || ''} />
                            )}
                            {price?.shop?.icon && !lazyShopLogo && <WaitingShopLogo />}
                            {price?.shop?.name || ''}
                          </ShopName>
                        </LinkComponent>
                      </HeroPriceLineLabel>
                      <HeroPriceLineCta>
                        <CtaHeroCard
                          price={price}
                          title={title}
                          eventCta={() => eventCta(price.price, price.shop.name, 'top-in-content', id)}
                        />
                      </HeroPriceLineCta>
                    </HeroPriceLine>
                  ) : null,
                )}
              </HeroPriceTable>
              {prices.length > 5 && (
                <HeroPriceMore onClick={() => getMorePrice()}>
                  <Rotator className={seeMorePrice ? 'mod-rotated' : null}>
                    <ArrowPlainDown width={21} height={21} color="var(--text-color-alt)" />
                  </Rotator>
                  <HeroPriceMoreLabel>
                    {seeMorePrice ? t('design.reviewCard.getLessPrice') : t('design.reviewCard.getMorePrice')}
                  </HeroPriceMoreLabel>
                </HeroPriceMore>
              )}
            </HeroPrice>
          ) : (
            t('design.reviewCard.noResultPrice')
          )}
        </HeroTop>
      </HeroBox>
      <HeroProsAndCons>
        {pros?.length > 0 ? (
          <LeftSide>
            <Title>{t('design.ratingBlock.pros')}</Title>
            <List>
              {pros?.map((pro: string) => (
                <ProItem key={pro}>
                  <Pro height={22} width={22} color="var(--theme-palette-green)" />
                  {pro}
                </ProItem>
              ))}
            </List>
          </LeftSide>
        ) : null}
        {cons?.length > 0 ? (
          <RightSide>
            <Title>{t('design.ratingBlock.cons')}</Title>
            <List>
              {cons?.map((con: string) => (
                <ConItem key={con}>
                  <Con height={22} width={22} color="var(--theme-palette-red)" />
                  {con}
                </ConItem>
              ))}
            </List>
          </RightSide>
        ) : null}
      </HeroProsAndCons>
      {testLink && (
        <HeroReadConclusion>
          <Cta className="mod-blue mod-empty" inline {...testLink}>
            {t('design.heroCard.conclusion')}
          </Cta>
        </HeroReadConclusion>
      )}
    </Hero>
  );
};

export default HeroCard;
